/**
 * Vizuális effektek: részecskék, nyomjelző csíkok, becsapódás-nyomok,
 * robbanások, dinamikus villanófények.
 *
 * Minden pool-ozott és fix méretű: egy hosszú harc közben sem allokálunk,
 * és nem lehet belőle memóriaszivárgás vagy akadás.
 *
 * Gazda: C
 */

import * as THREE from 'three'
import type { ImpactKind } from '../core/contracts'
import type { Rng } from '../core/rng'

const MAX_PARTICLES = 1400
const MAX_TRACERS = 48
const MAX_DECALS = 120
const MAX_FLASH_LIGHTS = 4

/**
 * Ugyanaz a fizikai fényegység-átváltás, mint a rendererben: a hívók
 * „mennyire világítson 1-2 egységről" értelemben adják meg az erősséget.
 */
const FLASH_UNIT = 1.6

// ---------------------------------------------------------------------------
// Részecskék
// ---------------------------------------------------------------------------

const PARTICLE_VERT = /* glsl */ `
attribute float aSize;
attribute vec3 aColor;
attribute float aAlpha;
varying vec3 vColor;
varying float vAlpha;
void main() {
  vColor = aColor;
  vAlpha = aAlpha;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = aSize * 420.0 / max(0.1, -mv.z);
  gl_Position = projectionMatrix * mv;
}
`

const PARTICLE_FRAG = /* glsl */ `
varying vec3 vColor;
varying float vAlpha;
void main() {
  vec2 d = gl_PointCoord - 0.5;
  float r = dot(d, d);
  if (r > 0.25) discard;
  // Kemény mag, lágy perem — a teljesen lágy folt „ködös", a kemény „papír".
  float a = vAlpha * smoothstep(0.25, 0.06, r);
  gl_FragColor = vec4(vColor, a);
}
`

interface Particle {
  active: boolean
  life: number
  maxLife: number
  size: number
  endSize: number
  gravity: number
  drag: number
  r: number
  g: number
  b: number
  /** Kioltáskor sötétedjen-e (szikra) vagy csak halványuljon (füst). */
  fadeToBlack: boolean
}

class ParticleSystem {
  readonly points: THREE.Points
  private pos: Float32Array
  private vel: Float32Array
  private col: Float32Array
  private size: Float32Array
  private alpha: Float32Array
  private data: Particle[] = []
  private cursor = 0
  private geo: THREE.BufferGeometry

  constructor() {
    this.pos = new Float32Array(MAX_PARTICLES * 3)
    this.vel = new Float32Array(MAX_PARTICLES * 3)
    this.col = new Float32Array(MAX_PARTICLES * 3)
    this.size = new Float32Array(MAX_PARTICLES)
    this.alpha = new Float32Array(MAX_PARTICLES)

    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.data.push({
        active: false,
        life: 0,
        maxLife: 1,
        size: 0.1,
        endSize: 0,
        gravity: 0,
        drag: 0,
        r: 1,
        g: 1,
        b: 1,
        fadeToBlack: false,
      })
      // Az inaktív részecskéket a kamera mögé rejtjük 0 alfával.
      this.alpha[i] = 0
    }

    this.geo = new THREE.BufferGeometry()
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3))
    this.geo.setAttribute('aColor', new THREE.BufferAttribute(this.col, 3))
    this.geo.setAttribute('aSize', new THREE.BufferAttribute(this.size, 1))
    this.geo.setAttribute('aAlpha', new THREE.BufferAttribute(this.alpha, 1))
    this.geo.setDrawRange(0, MAX_PARTICLES)
    this.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6)

    const mat = new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })

    this.points = new THREE.Points(this.geo, mat)
    this.points.frustumCulled = false
    this.points.renderOrder = 5
  }

  spawn(
    x: number,
    y: number,
    z: number,
    vx: number,
    vy: number,
    vz: number,
    color: number,
    size: number,
    endSize: number,
    life: number,
    gravity: number,
    drag: number,
    fadeToBlack: boolean,
  ): void {
    // Körkörös foglalás: ha elfogyott a keret, a legrégebbit írjuk felül.
    // Ez jobb, mint eldobni az új effektet — a friss esemény fontosabb.
    const i = this.cursor
    this.cursor = (this.cursor + 1) % MAX_PARTICLES

    const p = this.data[i]
    p.active = true
    p.life = life
    p.maxLife = life
    p.size = size
    p.endSize = endSize
    p.gravity = gravity
    p.drag = drag
    p.fadeToBlack = fadeToBlack
    p.r = ((color >> 16) & 255) / 255
    p.g = ((color >> 8) & 255) / 255
    p.b = (color & 255) / 255

    const i3 = i * 3
    this.pos[i3] = x
    this.pos[i3 + 1] = y
    this.pos[i3 + 2] = z
    this.vel[i3] = vx
    this.vel[i3 + 1] = vy
    this.vel[i3 + 2] = vz
  }

  update(dt: number): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this.data[i]
      if (!p.active) continue
      p.life -= dt
      if (p.life <= 0) {
        p.active = false
        this.alpha[i] = 0
        continue
      }

      const i3 = i * 3
      const damp = Math.max(0, 1 - p.drag * dt)
      this.vel[i3] *= damp
      this.vel[i3 + 1] = this.vel[i3 + 1] * damp - p.gravity * dt
      this.vel[i3 + 2] *= damp
      this.pos[i3] += this.vel[i3] * dt
      this.pos[i3 + 1] += this.vel[i3 + 1] * dt
      this.pos[i3 + 2] += this.vel[i3 + 2] * dt

      const t = p.life / p.maxLife
      this.size[i] = p.endSize + (p.size - p.endSize) * t
      this.alpha[i] = t
      const k = p.fadeToBlack ? t : 1
      this.col[i3] = p.r * k
      this.col[i3 + 1] = p.g * k
      this.col[i3 + 2] = p.b * k
    }

    this.geo.attributes.position.needsUpdate = true
    this.geo.attributes.aColor.needsUpdate = true
    this.geo.attributes.aSize.needsUpdate = true
    this.geo.attributes.aAlpha.needsUpdate = true
  }

  clear(): void {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.data[i].active = false
      this.alpha[i] = 0
    }
  }

  dispose(): void {
    this.geo.dispose()
    ;(this.points.material as THREE.Material).dispose()
  }
}

// ---------------------------------------------------------------------------
// Nyomjelző csíkok
// ---------------------------------------------------------------------------

interface Tracer {
  mesh: THREE.Mesh
  life: number
  maxLife: number
}

class TracerPool {
  readonly group = new THREE.Group()
  private items: Tracer[] = []
  private cursor = 0

  constructor() {
    const geo = new THREE.CylinderGeometry(1, 1, 1, 4, 1, true)
    geo.rotateX(Math.PI / 2)
    geo.translate(0, 0, 0.5)
    for (let i = 0; i < MAX_TRACERS; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.visible = false
      mesh.frustumCulled = false
      this.group.add(mesh)
      this.items.push({ mesh, life: 0, maxLife: 1 })
    }
    this.group.renderOrder = 4
  }

  fire(from: THREE.Vector3, to: THREE.Vector3, color: number, width: number, life: number): void {
    const t = this.items[this.cursor]
    this.cursor = (this.cursor + 1) % MAX_TRACERS
    const dir = to.clone().sub(from)
    const len = dir.length()
    if (len < 1e-4) return

    t.mesh.position.copy(from)
    t.mesh.lookAt(to)
    t.mesh.scale.set(width, width, len)
    t.mesh.visible = true
    const mat = t.mesh.material as THREE.MeshBasicMaterial
    mat.color.setHex(color)
    mat.opacity = 1
    t.life = life
    t.maxLife = life
  }

  update(dt: number): void {
    for (const t of this.items) {
      if (t.life <= 0) continue
      t.life -= dt
      const mat = t.mesh.material as THREE.MeshBasicMaterial
      if (t.life <= 0) {
        t.mesh.visible = false
        mat.opacity = 0
        continue
      }
      const k = t.life / t.maxLife
      mat.opacity = k
      // A csík elvékonyodik, ahogy halványul — így „elszáll", nem eltűnik.
      t.mesh.scale.x = t.mesh.scale.y = Math.max(0.005, t.mesh.scale.x * (0.94 + 0.06 * k))
    }
  }

  clear(): void {
    for (const t of this.items) {
      t.life = 0
      t.mesh.visible = false
    }
  }

  dispose(): void {
    // A geometria közös: pontosan egyszer szabadítjuk fel.
    this.items[0]?.mesh.geometry.dispose()
    for (const t of this.items) (t.mesh.material as THREE.Material).dispose()
    this.items.length = 0
    this.group.clear()
  }
}

// ---------------------------------------------------------------------------
// Becsapódás-nyomok (decal)
// ---------------------------------------------------------------------------

interface Decal {
  mesh: THREE.Mesh
  life: number
  maxLife: number
}

class DecalPool {
  readonly group = new THREE.Group()
  private items: Decal[] = []
  private cursor = 0

  constructor() {
    const geo = new THREE.PlaneGeometry(1, 1)
    for (let i = 0; i < MAX_DECALS; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -4,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.visible = false
      this.group.add(mesh)
      this.items.push({ mesh, life: 0, maxLife: 1 })
    }
    this.group.renderOrder = 2
  }

  place(point: THREE.Vector3, normal: THREE.Vector3, color: number, size: number, life: number): void {
    const d = this.items[this.cursor]
    this.cursor = (this.cursor + 1) % MAX_DECALS

    // Kicsit a felület elé toljuk, hogy ne Z-harcoljon.
    d.mesh.position.copy(point).addScaledVector(normal, 0.012)
    d.mesh.lookAt(d.mesh.position.clone().add(normal))
    d.mesh.rotateZ(((point.x * 12.9898 + point.z * 78.233) % 1) * Math.PI * 2)
    d.mesh.scale.set(size, size, 1)
    d.mesh.visible = true
    const mat = d.mesh.material as THREE.MeshBasicMaterial
    mat.color.setHex(color)
    mat.opacity = 0.9
    d.life = life
    d.maxLife = life
  }

  update(dt: number): void {
    for (const d of this.items) {
      if (d.life <= 0) continue
      d.life -= dt
      const mat = d.mesh.material as THREE.MeshBasicMaterial
      if (d.life <= 0) {
        d.mesh.visible = false
        mat.opacity = 0
        continue
      }
      // Csak az utolsó 20%-ban halványul — addig „ott van", ami fontos:
      // a harc nyomai a térben maradjanak.
      const k = d.life / d.maxLife
      mat.opacity = 0.9 * Math.min(1, k / 0.2)
    }
  }

  clear(): void {
    for (const d of this.items) {
      d.life = 0
      d.mesh.visible = false
    }
  }

  dispose(): void {
    this.items[0]?.mesh.geometry.dispose()
    for (const d of this.items) (d.mesh.material as THREE.Material).dispose()
    this.items.length = 0
    this.group.clear()
  }
}

// ---------------------------------------------------------------------------
// Villanófények
// ---------------------------------------------------------------------------

interface FlashLight {
  light: THREE.PointLight
  life: number
  maxLife: number
  peak: number
}

class FlashPool {
  readonly group = new THREE.Group()
  private items: FlashLight[] = []
  private cursor = 0

  constructor() {
    for (let i = 0; i < MAX_FLASH_LIGHTS; i++) {
      // A fény SOSEM válik láthatatlanná, csak nulla erősségűvé.
      //
      // Ez nem szépészeti döntés: a Three.js a LÁTHATÓ fények számából
      // képzi a shader-program kulcsát. Ha egy fény ki-be kapcsolódik,
      // megváltozik a fényszám, és a motor ÚJRAFORDÍTJA a jelenet összes
      // anyagának shaderét. Lövésenként. Ez több száz milliszekundumos
      // főszál-blokkolást okoz — ami nemcsak akadás, hanem a böngésző
      // el is engedi tőle az egérzárat.
      const light = new THREE.PointLight(0xffffff, 0, 12, 2)
      this.group.add(light)
      this.items.push({ light, life: 0, maxLife: 1, peak: 0 })
    }
  }

  flash(pos: THREE.Vector3, color: number, intensity: number, distance: number, life: number): void {
    const f = this.items[this.cursor]
    this.cursor = (this.cursor + 1) % MAX_FLASH_LIGHTS
    f.light.position.copy(pos)
    f.light.color.setHex(color)
    f.light.distance = distance
    const scaled = intensity * FLASH_UNIT
    f.light.intensity = scaled
    f.peak = scaled
    f.life = life
    f.maxLife = life
  }

  update(dt: number): void {
    for (const f of this.items) {
      if (f.life <= 0) continue
      f.life -= dt
      if (f.life <= 0) {
        f.light.intensity = 0
        continue
      }
      const k = f.life / f.maxLife
      f.light.intensity = f.peak * k * k
    }
  }

  clear(): void {
    for (const f of this.items) {
      f.life = 0
      f.light.intensity = 0
    }
  }

  dispose(): void {
    for (const f of this.items) f.light.dispose()
    this.items.length = 0
    this.group.clear()
  }
}

// ---------------------------------------------------------------------------
// Összefogó effekt-réteg
// ---------------------------------------------------------------------------

/** Becsapódás-jellegenkénti megjelenés — ettől „érződik" az anyag. */
const IMPACT_STYLE: Record<ImpactKind, { spark: number; smoke: number; decal: number; decalSize: number; count: number }> = {
  concrete: { spark: 0xc09759, smoke: 0x5d646c, decal: 0x15181c, decalSize: 0.3, count: 9 },
  metal: { spark: 0xffd98a, smoke: 0x787f88, decal: 0x0a0b0d, decalSize: 0.22, count: 14 },
  flesh: { spark: 0xa53347, smoke: 0x330a13, decal: 0x2a0004, decalSize: 0.42, count: 12 },
  energy: { spark: 0x8affff, smoke: 0x235664, decal: 0x122b33, decalSize: 0.34, count: 16 },
}

const _flashPos = new THREE.Vector3()

export class Effects {
  readonly group = new THREE.Group()
  private particles = new ParticleSystem()
  private tracers = new TracerPool()
  private decals = new DecalPool()
  private flashes = new FlashPool()
  private rng: Rng

  constructor(rng: Rng) {
    this.rng = rng
    this.group.add(this.particles.points, this.tracers.group, this.decals.group, this.flashes.group)
    this.group.name = 'effects'
  }

  update(dt: number): void {
    this.particles.update(dt)
    this.tracers.update(dt)
    this.decals.update(dt)
    this.flashes.update(dt)
  }

  clear(): void {
    this.particles.clear()
    this.tracers.clear()
    this.decals.clear()
    this.flashes.clear()
  }

  /**
   * Előmelegítés: minden pool-elemet láthatóvá tesz, hogy a shaderük
   * lefordulhasson. A hívó ezután rajzoltat egy képkockát, majd
   * `clear()`-rel visszaállít.
   *
   * Miért kell: a `WebGLRenderer.compile()` `traverseVisible`-lel jár,
   * vagyis a `visible = false` pool-elemeket KIHAGYJA. Emiatt a nyomjelző,
   * a becsapódás-nyom és a robbanás shadere csak az ELSŐ lövéskor fordult
   * le — pont a harc közben, több száz milliszekundumos főszál-blokkolással.
   */
  prewarmVisible(): void {
    this.group.traverse((o) => {
      o.visible = true
    })
    // Egyetlen részecske minden fajtából, hogy a pontfelhő ne üresen
    // renderelődjön (az üres tartomány egyes meghajtókon nem fordít).
    this.impact(_flashPos.set(0, -900, 0), new THREE.Vector3(0, 1, 0), 'metal')
    this.tracer(new THREE.Vector3(0, -900, 0), new THREE.Vector3(0, -899, 0), 0xffffff)
  }

  /** Végleges felszabadítás (X kérése — `09-X-revizio.md` F7). */
  dispose(): void {
    this.particles.dispose()
    this.tracers.dispose()
    this.decals.dispose()
    this.flashes.dispose()
    this.group.clear()
  }

  impact(point: THREE.Vector3, normal: THREE.Vector3, kind: ImpactKind): void {
    const st = IMPACT_STYLE[kind] ?? IMPACT_STYLE.concrete
    const r = this.rng

    for (let i = 0; i < st.count; i++) {
      // A szikrák a normális köré szóródnak, nem gömbszerűen — így látszik,
      // hogy egy felületről pattannak vissza.
      const vx = normal.x * 2.4 + r.spread(2.2)
      const vy = normal.y * 2.4 + r.spread(2.2) + 1.1
      const vz = normal.z * 2.4 + r.spread(2.2)
      this.particles.spawn(
        point.x, point.y, point.z,
        vx, vy, vz,
        st.spark,
        0.05 + r.next() * 0.04,
        0.005,
        0.18 + r.next() * 0.3,
        7,
        2.5,
        true,
      )
    }
    // Néhány lassú füstpamacs — ez adja a súlyt a becsapódásnak.
    for (let i = 0; i < 3; i++) {
      this.particles.spawn(
        point.x, point.y, point.z,
        normal.x * 0.7 + r.spread(0.5),
        normal.y * 0.7 + r.spread(0.5) + 0.5,
        normal.z * 0.7 + r.spread(0.5),
        st.smoke,
        0.1,
        0.35,
        0.45 + r.next() * 0.3,
        -0.4,
        1.6,
        false,
      )
    }

    if (kind !== 'energy') {
      this.decals.place(point, normal, st.decal, st.decalSize * (0.7 + r.next() * 0.6), 26)
    }
    // A villanást ELTOLJUK a felülettől: a d² csillapítás miatt egy pontosan
    // a falon ülő fény gyakorlatilag végtelen fényerőt adna ott.
    _flashPos.copy(point).addScaledVector(normal, 0.45)
    this.flashes.flash(_flashPos, st.spark, 1.6, 4, 0.09)
  }

  blood(point: THREE.Vector3, dir: THREE.Vector3, amount: number): void {
    const r = this.rng
    const n = Math.min(26, 5 + Math.floor(amount * 0.5))
    for (let i = 0; i < n; i++) {
      this.particles.spawn(
        point.x, point.y, point.z,
        dir.x * 2.5 + r.spread(2.4),
        dir.y * 1.5 + r.spread(2.0) + 1.4,
        dir.z * 2.5 + r.spread(2.4),
        r.chance(0.75) ? 0x5c0a0f : 0xa53347,
        0.07 + r.next() * 0.05,
        0.02,
        0.4 + r.next() * 0.4,
        9,
        1.2,
        false,
      )
    }
  }

  muzzleFlash(pos: THREE.Vector3, dir: THREE.Vector3, color: number, radius: number): void {
    const r = this.rng
    this.flashes.flash(pos, color, 4.5, radius, 0.07)
    for (let i = 0; i < 6; i++) {
      this.particles.spawn(
        pos.x, pos.y, pos.z,
        dir.x * (6 + r.next() * 6) + r.spread(1.6),
        dir.y * (6 + r.next() * 6) + r.spread(1.6),
        dir.z * (6 + r.next() * 6) + r.spread(1.6),
        color,
        0.11,
        0.01,
        0.06 + r.next() * 0.05,
        0,
        7,
        true,
      )
    }
  }

  explosion(point: THREE.Vector3, radius: number, color = 0xffa030): void {
    const r = this.rng
    this.flashes.flash(point, color, 26, radius * 3.2, 0.4)

    // Tűzmag
    for (let i = 0; i < 46; i++) {
      const sp = 3 + r.next() * radius * 2.4
      const th = r.next() * Math.PI * 2
      const ph = Math.acos(r.range(-1, 1))
      this.particles.spawn(
        point.x, point.y, point.z,
        Math.sin(ph) * Math.cos(th) * sp,
        Math.cos(ph) * sp + 2,
        Math.sin(ph) * Math.sin(th) * sp,
        r.chance(0.5) ? 0xffd98a : color,
        0.3 + r.next() * 0.25,
        0.03,
        0.3 + r.next() * 0.35,
        3,
        2.2,
        true,
      )
    }
    // Felszálló füstgomba — a robbanás utóélete, enélkül „csak villan".
    for (let i = 0; i < 22; i++) {
      const th = r.next() * Math.PI * 2
      const sp = r.next() * radius * 0.9
      this.particles.spawn(
        point.x, point.y, point.z,
        Math.cos(th) * sp,
        1 + r.next() * 2.5,
        Math.sin(th) * sp,
        0x32373d,
        0.35,
        0.95,
        0.9 + r.next() * 0.7,
        -0.8,
        1.5,
        false,
      )
    }
  }

  tracer(from: THREE.Vector3, to: THREE.Vector3, color: number, width = 0.016): void {
    this.tracers.fire(from, to, color, width, 0.075)
  }

  /** Halálkor szétrepülő törmelék — a hullán túl ez adja a „megölted" élményt. */
  gib(point: THREE.Vector3, color: number, count: number): void {
    const r = this.rng
    for (let i = 0; i < count; i++) {
      this.particles.spawn(
        point.x, point.y, point.z,
        r.spread(4),
        r.range(1, 6),
        r.spread(4),
        color,
        0.09 + r.next() * 0.07,
        0.02,
        0.7 + r.next() * 0.6,
        11,
        0.6,
        false,
      )
    }
  }

  /** Lövedék nyomvonala — folyamatosan hívva a repülés alatt. */
  projectileTrail(pos: THREE.Vector3, color: number, size: number): void {
    const r = this.rng
    this.particles.spawn(
      pos.x + r.spread(0.04),
      pos.y + r.spread(0.04),
      pos.z + r.spread(0.04),
      r.spread(0.35),
      r.spread(0.35) + 0.2,
      r.spread(0.35),
      color,
      size,
      0.005,
      0.22,
      -0.5,
      2.4,
      true,
    )
  }

  /** Környezeti szikra/pára — a tér életben tartásához. */
  ambientMote(pos: THREE.Vector3, color: number): void {
    const r = this.rng
    this.particles.spawn(
      pos.x, pos.y, pos.z,
      r.spread(0.25),
      r.range(0.1, 0.5),
      r.spread(0.25),
      color,
      0.035,
      0.01,
      1.4 + r.next() * 1.2,
      -0.15,
      0.6,
      false,
    )
  }
}
